/**
 * Client-side idempotency dedup (Card #149).
 *
 * Symptom this guards against:
 *   Operator on industrial WiFi: types "confirm WO open for press 4 reducer".
 *   Wifi blips for ~1s right after the network call. The chat client retries
 *   automatically (or the operator double-taps). Backend creates TWO identical
 *   work orders. Supervisor finds duplicates in SAP next morning.
 *
 * Strategy:
 *   1. Each outbound chat message gets a fresh `clientRequestId` (UUID v4).
 *   2. The frontend keeps a Set of recently-sent ids with a 1h TTL.
 *   3. If sendRaw is called twice with the same id (component remount,
 *      double-tap, retry), the second call is dropped before the network
 *      call.
 *   4. The id travels in the request body so the backend can also dedup
 *      across reconnect — see `routes/chat-stream.ts` and `gateway/server.ts`.
 *
 * TTL chosen to match the backend Redis idempotency cache (3600s) so neither
 * side can reject a payload the other side still considers fresh.
 */

const ONE_HOUR_MS = 60 * 60 * 1000

interface SeenEntry {
  expiresAt: number
}

/** Module-level Map. One entry per known clientRequestId. */
const seen = new Map<string, SeenEntry>()

/** Drop entries whose TTL elapsed. Cheap O(n) sweep. */
function gc(now: number): void {
  for (const [id, entry] of seen) {
    if (entry.expiresAt <= now) {
      seen.delete(id)
    }
  }
}

/**
 * Generate a fresh clientRequestId (UUID v4) for a new outbound message.
 *
 * Uses `crypto.randomUUID()` which is widely available (every supported
 * browser + Node 20). No fallback needed in this codebase — it is already
 * used unconditionally in connectionStore.sendFileUpload.
 */
export function generateClientRequestId(): string {
  return crypto.randomUUID()
}

/**
 * Return `true` if this id was NOT previously seen, register it, and return.
 * Return `false` (and DO NOT register) if the id is a duplicate within TTL.
 *
 * Caller pattern:
 *   if (!registerClientRequestId(id)) return false  // skip enqueue
 *   ...continue sending...
 */
export function registerClientRequestId(id: string): boolean {
  const now = Date.now()
  gc(now)
  const existing = seen.get(id)
  if (existing && existing.expiresAt > now) {
    return false
  }
  seen.set(id, { expiresAt: now + ONE_HOUR_MS })
  return true
}

/** Test-only: drop all known ids. */
export function _resetIdempotencyForTest(): void {
  seen.clear()
}

/** Test-only: peek at how many ids are currently tracked. */
export function _idempotencySize(): number {
  return seen.size
}
